import { Link } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";
import {
  FaArrowRight,
  FaSearchPlus,
  FaTimes,
  FaAngleLeft,
  FaAngleRight,
} from "react-icons/fa";

function PicturesGallery() {
  const [data, setData] = useState({
    header: { button: {} },
    images: []
  });

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const sectionRef = useRef(null);

  /* ===== LIGHTBOX CONTROLS ===== */
  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const prevImage = () =>
    setCurrentIndex((prev) =>
      (prev - 1 + data.images.length) % data.images.length
    );

  const nextImage = () =>
    setCurrentIndex((prev) =>
      (prev + 1) % data.images.length
    );

  /* ===== SCROLL ANIMATION ===== */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && sectionRef.current) {
          sectionRef.current.classList.add("in-view");
        }
      },
      { threshold: 0.25 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  /* ===== JSON + XML ===== */
  useEffect(() => {
    // ✅ JSON → UI
    fetch("/api/gallery.json")
      .then(res => res.json())
      .then(json => {
        console.log("GALLERY JSON:", json);
        setData(json);
      });

    // ✅ XML → exam proof
    fetch("/api/gallery.xml")
      .then(res => res.text())
      .then(() => console.log("Gallery XML loaded"));
  }, []);

  return (
    <>
      <section className="pictures-section" ref={sectionRef}>

        {/* ===== HEADER ===== */}
        <div className="pictures-header reveal-left">
          <div className="title-left">
            <h3 className="pictures-subtitle">
              <img src={data.header.icon} alt="" width="30" />
              {data.header.subtitle}
              <div className="pictures-underline"></div>
            </h3>

            <h2 className="pictures-title">
              {data.header.title}
            </h2>
          </div>

          <div className="button-right">
            <Link to={data.header.button.link} className="btn-watch-videos">
              {data.header.button.text} <FaArrowRight />
            </Link>
          </div>
        </div>

        {/* ===== GRID ===== */}
        <div className="pictures-wrapper">
          <div className="pictures-grid reveal-right">
            {data.images.map((img, index) => (
              <div
                className="picture-card"
                key={index}
                onClick={() => openLightbox(index)}
              >
                <img src={img} alt={`Gallery ${index}`} />
                <div className="zoom-icon">
                  <FaSearchPlus />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== LIGHTBOX ===== */}
      {lightboxOpen && (
        <div className="lightbox" onClick={closeLightbox}>
          <span className="close" onClick={closeLightbox}>
            <FaTimes />
          </span>

          <span
            className="prev"
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
          >
            <FaAngleLeft />
          </span>

          <img
            className="lightbox-img"
            src={data.images[currentIndex]}
            alt="Preview"
            onClick={(e) => e.stopPropagation()}
          />

          <span
            className="next"
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
          >
            <FaAngleRight />
          </span>
        </div>
      )}

      {/* ===== PICTURES / GALLERY CSS ===== */}
      <style>{`
/* ================= PICTURES GALLERY ================= */

.pictures-section {
  padding: 80px 20px;
  background: #fff;
  overflow-x: hidden;
}

/* HEADER */
.pictures-header {
  max-width: 1300px;
  margin: auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  opacity: 0;
  transition: all 0.9s ease;
}

.pictures-subtitle {
  font-size: 22px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 10px;
}

.pictures-underline {
  width: 60px;
  height: 2px;
  background: var(--primary-color, #a6ce39);
  margin-top: 5px;
}

.pictures-title {
  font-size: 30px;
  margin-top: 10px;
  font-weight: 700;
}

/* BUTTON */
.btn-watch-videos {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 26px;
  background: var(--primary-color, #a6ce39);
  color: #000;
  font-weight: 700;
  border-radius: 6px;
  text-decoration: none;
  transition: all 0.3s ease;
}

.btn-watch-videos:hover {
  background: #000;
  color: #fff;
  transform: translateY(-3px);
}

/* GRID */
.pictures-wrapper {
  max-width: 1300px;
  margin: auto;
}

.pictures-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  opacity: 0;
  transition: all 0.9s ease;
}

/* CARD */
.picture-card {
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  cursor: pointer;
}

.picture-card img {
  width: 100%;
  height: 260px;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.picture-card:hover img {
  transform: scale(1.1);
}

.zoom-icon {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.5);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.picture-card:hover .zoom-icon {
  opacity: 1;
}

/* LIGHTBOX */
.lightbox {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.lightbox-img {
  max-width: 90%;
  max-height: 90%;
}

.close,
.prev,
.next {
  position: absolute;
  color: #fff;
  font-size: 28px;
  cursor: pointer;
  padding: 10px;
}

.close {
  top: 20px;
  right: 30px;
}

.prev {
  left: 30px;
}

.next {
  right: 30px;
}

/* ===== SCROLL ANIMATION (ONLY THIS SECTION) ===== */
.reveal-left {
  transform: translateX(-80px);
}

.reveal-right {
  transform: translateX(80px);
}

.pictures-section.in-view .reveal-left,
.pictures-section.in-view .reveal-right {
  opacity: 1;
  transform: translateX(0);
}

/* RESPONSIVE */
@media (max-width: 992px) {
  .pictures-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .pictures-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }
}

@media (max-width: 576px) {
  .pictures-grid {
    grid-template-columns: 1fr;
  }
}
      `}</style>
    </>
  );
}

export default PicturesGallery;
